import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    type AnalyticsScenarioParams,
    parseStoredAnalyticsScenario,
    scenarioSessionStorageKey,
    serializeAnalyticsScenario,
} from 'src/lib/analytics-scenario'

/** React context value: applied scenario (or null) and helpers to update or clear it. */
export type ScenarioContextValue = {
    appliedScenario: AnalyticsScenarioParams | null
    applyScenario: (params: AnalyticsScenarioParams) => void
    resetToDefaults: () => void
    hasOverrides: boolean
}

const ScenarioContext = createContext<ScenarioContextValue | null>(null)

type ScenarioProviderProps = {
    children: ReactNode
    /** When set, scenario is read from and written to `sessionStorage` for this org (browser tab). */
    sessionOrgId?: string
}

/** Holds admin scenario state for the authed app; analytics routes read `appliedScenario` for API calls. */
export function ScenarioProvider({children, sessionOrgId}: ScenarioProviderProps) {
    const [appliedScenario, setAppliedScenario] = useState<AnalyticsScenarioParams | null>(null)

    useEffect(() => {
        if (!sessionOrgId) {
            setAppliedScenario(null)
            return
        }
        const raw = sessionStorage.getItem(scenarioSessionStorageKey(sessionOrgId))
        if (!raw) {
            setAppliedScenario(null)
            return
        }
        setAppliedScenario(parseStoredAnalyticsScenario(raw))
    }, [sessionOrgId])

    const applyScenario = useCallback(
        (params: AnalyticsScenarioParams) => {
            setAppliedScenario(params)
            if (sessionOrgId) {
                sessionStorage.setItem(
                    scenarioSessionStorageKey(sessionOrgId),
                    serializeAnalyticsScenario(params),
                )
            }
        },
        [sessionOrgId],
    )

    const resetToDefaults = useCallback(() => {
        setAppliedScenario(null)
        if (sessionOrgId) {
            sessionStorage.removeItem(scenarioSessionStorageKey(sessionOrgId))
        }
    }, [sessionOrgId])

    const value = useMemo(
        () => ({
            appliedScenario,
            applyScenario,
            resetToDefaults,
            hasOverrides: appliedScenario !== null,
        }),
        [appliedScenario, applyScenario, resetToDefaults],
    )

    return <ScenarioContext.Provider value={value}>{children}</ScenarioContext.Provider>
}

/** Access scenario state; must be used under `ScenarioProvider` (layout wraps authed routes). */
export function useScenarioPlanning(): ScenarioContextValue {
    const ctx = useContext(ScenarioContext)
    if (!ctx) {
        throw new Error('useScenarioPlanning must be used within ScenarioProvider')
    }
    return ctx
}
